import { useState } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { ACCOUNT_TYPE_META, CONTACT_ROLE_LABEL } from "../lib/accountTypes";
import { STAGE_LABELS } from "../lib/crmHealth";
import VoiceCapture from "./VoiceCapture";
import { Waveform, X, CircleNotch, CheckSquare, Square, WarningCircle, ArrowRight, UserPlus, Note, ListChecks, CalendarCheck, PencilSimple } from "@phosphor-icons/react";

const FIELD_LABEL = {
    name: "Name", address: "Address", phone: "Phone", email: "Email", website: "Website",
    stage: "Sales stage", type: "Account type", contact_every_days: "Contact every (days)",
    title: "Title", role: "Role", is_primary: "Primary contact",
};
const EXAMPLES = [
    "Change the address to 1450 Lake Street, Wayzata MN 55391",
    "Sarah's new mobile is 612 555 0182 and she's now Head of Facilities",
    "Make Mark the primary contact and move them to negotiation",
    "Add Tom Briggs, maintenance supervisor, tom@example.com",
];

function show(field, v) {
    if (v === "" || v === null || v === undefined) return "—";
    if (field === "stage") return STAGE_LABELS[v] || v;
    if (field === "type") return ACCOUNT_TYPE_META[v]?.label || v;
    if (field === "role") return CONTACT_ROLE_LABEL[v] || v;
    if (field === "is_primary") return v ? "Yes" : "No";
    if (field === "contact_every_days") return Number(v) === 0 ? "Don't flag" : `${v} days`;
    return String(v);
}

export function VoiceUpdateButton({ client, onDone }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button className="btn btn-ghost" onClick={() => setOpen(true)} data-testid="voice-update-open-btn" title="Update this record by talking">
                <Waveform size={14} weight="bold" /> Update by voice
            </button>
            {open && <VoiceUpdateModal client={client} onClose={(done) => { setOpen(false); if (done) onDone?.(); }} />}
        </>
    );
}

export default function VoiceUpdateModal({ client, onClose }) {
    const [command, setCommand] = useState("");
    const [step, setStep] = useState("ask"); // ask | thinking | review | applying | done
    const [err, setErr] = useState("");
    const [res, setRes] = useState(null);
    const [items, setItems] = useState([]);
    const [applied, setApplied] = useState([]);

    async function propose(text) {
        const cmd = (text ?? command).trim();
        if (cmd.length < 4) return;
        setCommand(cmd);
        setErr(""); setStep("thinking");
        try {
            const { data } = await endpoints.proposeVoiceUpdate(client.id, { command: cmd });
            setRes(data);
            setItems(data.changes.map((c) => ({ ...c, on: true })));
            setStep("review");
        } catch (e) { setErr(formatApiError(e)); setStep("ask"); }
    }

    async function apply() {
        setErr(""); setStep("applying");
        try {
            const { data } = await endpoints.applyVoiceUpdate(client.id, { command, changes: items.filter((i) => i.on).map(({ on, ...c }) => c) });
            setApplied(data.applied);
            setStep("done");
        } catch (e) { setErr(formatApiError(e)); setStep("review"); }
    }

    const setItem = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const count = items.filter((i) => i.on).length;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="voice-update-modal">
            <div className="rom-card w-full max-w-lg max-h-[94vh] flex flex-col">
                <div className="rom-card-head shrink-0">
                    <h3 className="flex items-center gap-1.5"><Waveform size={15} weight="bold" className="text-[var(--rom-orange)]" /> Update {client.name}</h3>
                    {step !== "thinking" && step !== "applying" && <button onClick={() => onClose(step === "done")}><X size={18} /></button>}
                </div>
                <div className="rom-card-body overflow-y-auto space-y-4">
                    {err && <div className="text-sm text-red-600 flex items-center gap-1.5" data-testid="voice-update-error"><WarningCircle size={14} weight="bold" /> {err}</div>}

                    {(step === "ask" || step === "thinking") && (
                        <>
                            <div className="p-4 rounded-[8px] border border-dashed border-[var(--rom-border-strong)] bg-slate-50/60">
                                <VoiceCapture label="Say what to change" maxSeconds={60} disabled={step === "thinking"} onTranscribed={(t) => { setCommand(t); propose(t); }} />
                            </div>
                            <div className="flex gap-2">
                                <input className="input flex-1" placeholder="…or type it" value={command} onChange={(e) => setCommand(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && propose()} disabled={step === "thinking"} data-testid="voice-update-input" />
                                <button className="btn btn-primary" onClick={() => propose()} disabled={step === "thinking" || command.trim().length < 4} data-testid="voice-update-go">
                                    {step === "thinking" ? <CircleNotch size={14} className="animate-spin" /> : "Go"}
                                </button>
                            </div>
                            <div className="space-y-1">
                                <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold">Try</div>
                                {EXAMPLES.map((x) => (
                                    <button key={x} className="block text-left text-xs text-[var(--rom-navy)] hover:underline" onClick={() => setCommand(x)} disabled={step === "thinking"}>“{x}”</button>
                                ))}
                            </div>
                        </>
                    )}

                    {(step === "review" || step === "applying") && res && (
                        <>
                            <div className="text-sm text-[var(--rom-text-2)] italic">“{res.command}”</div>
                            {items.length === 0 && !res.unclear && <div className="text-sm text-[var(--rom-text-3)]">No changes needed — the record already matches.</div>}
                            <div className="space-y-2">
                                {items.map((c, i) => <ChangeRow key={i} c={c} onToggle={() => setItem(i, { on: !c.on })} onEdit={(v) => setItem(i, { value: v })} i={i} />)}
                            </div>
                            {(res.unclear || res.skipped.length > 0) && (
                                <div className="text-xs rounded-[6px] border border-amber-300 bg-amber-50 p-2.5 space-y-0.5" data-testid="voice-update-unclear">
                                    {res.unclear && <div><WarningCircle size={12} weight="bold" className="inline text-amber-600" /> {res.unclear}</div>}
                                    {res.skipped.map((s) => <div key={s}>Skipped: {s}</div>)}
                                </div>
                            )}
                            <div className="flex items-center justify-between gap-2">
                                <button className="btn btn-ghost" onClick={() => setStep("ask")} disabled={step === "applying"}>Rephrase</button>
                                <button className="btn btn-primary" onClick={apply} disabled={step === "applying" || count === 0} data-testid="voice-update-apply">
                                    {step === "applying" ? <CircleNotch size={14} className="animate-spin" /> : `Apply ${count} change${count === 1 ? "" : "s"}`}
                                </button>
                            </div>
                        </>
                    )}

                    {step === "done" && (
                        <div className="py-4 space-y-3 text-center" data-testid="voice-update-done">
                            <div className="text-lg font-bold">Updated</div>
                            <ul className="text-sm text-[var(--rom-text-2)] space-y-0.5">{applied.map((a) => <li key={a}>{a}</li>)}</ul>
                            <p className="text-[11px] text-[var(--rom-text-3)]">Logged on the account's activity timeline.</p>
                            <div className="flex justify-center gap-2">
                                <button className="btn btn-ghost" onClick={() => { setCommand(""); setRes(null); setStep("ask"); }}>Another change</button>
                                <button className="btn btn-primary" onClick={() => onClose(true)} data-testid="voice-update-close">Done</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ChangeRow({ c, onToggle, onEdit, i }) {
    const [editing, setEditing] = useState(false);
    const editable = (c.kind === "account_field" || c.kind === "contact_update") && !["stage", "type", "role", "is_primary"].includes(c.field);
    let icon = PencilSimple, title, from, to;
    if (c.kind === "account_field") { title = FIELD_LABEL[c.field] || c.field; from = show(c.field, c.current); to = show(c.field, c.value); }
    else if (c.kind === "contact_update") { title = `${c.contact_name} · ${FIELD_LABEL[c.field] || c.field}`; from = show(c.field, c.current); to = show(c.field, c.value); }
    else if (c.kind === "contact_add") { icon = UserPlus; title = "New contact"; to = [c.contact.name, c.contact.title, c.contact.email, c.contact.phone].filter(Boolean).join(" · "); }
    else if (c.kind === "note") { icon = Note; title = "Add note"; to = c.value; }
    else { icon = c.kind === "task" ? ListChecks : CalendarCheck; title = c.kind === "task" ? "New task" : "New booking"; to = `${c.title} — ${c.due_date}${c.time ? ` ${c.time}` : ""}`; }
    const Icon = icon;
    return (
        <div className={`p-2.5 rounded-[6px] border flex items-start gap-2 ${c.on ? "border-[var(--rom-navy)] bg-white" : "border-[var(--rom-border)] bg-slate-50 opacity-60"}`} data-testid={`voice-change-${i}`}>
            <button onClick={onToggle} className="mt-0.5 shrink-0" data-testid={`voice-change-tick-${i}`}>
                {c.on ? <CheckSquare size={18} weight="fill" className="text-[var(--rom-navy)]" /> : <Square size={18} className="text-[var(--rom-text-3)]" />}
            </button>
            <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--rom-text-3)] flex items-center gap-1"><Icon size={12} weight="bold" /> {title}</div>
                {editing ? (
                    <input className="input h-8 mt-1 text-sm" autoFocus value={c.value} onChange={(e) => onEdit(e.target.value)} onBlur={() => setEditing(false)} onKeyDown={(e) => e.key === "Enter" && setEditing(false)} />
                ) : (
                    <div className="text-sm mt-0.5 flex items-center gap-1.5 flex-wrap">
                        {from !== undefined && <><span className="line-through text-[var(--rom-text-3)]">{from}</span><ArrowRight size={11} className="text-[var(--rom-text-3)]" /></>}
                        <span className="font-semibold">{to}</span>
                        {editable && c.on && <button onClick={() => setEditing(true)} className="text-[var(--rom-text-3)] hover:text-[var(--rom-navy)]" title="Edit value"><PencilSimple size={12} /></button>}
                    </div>
                )}
            </div>
        </div>
    );
}
